import { Routes } from '@angular/router';
import { FullpageLayoutComponent } from './layout/fullpage.layout/fullpage.layout.component';
import { AppLoaderComponent } from './layout/app-loader/app-loader.component';
import {
  AuthGuard,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { DashboardLayoutComponent } from './layout/dashboard.layout/dashboard.layout.component';
import { IsNotDeletedGuard } from './modules/auth/guards/is-not-deleted.guard';
import { LoadingContractLayoutComponent } from './modules/contracting/layouts/loading-contract-layout/loading-contract-layout.component';
import { NewContractComponent } from './modules/contracting/components/newContract/newContract.component';
import { ContractingAccesComponent } from './modules/contracting/components/contracting-acces/contracting-acces.component';
import { ContractingCompletedComponent } from './modules/contracting/components/contracting-completed/contracting-completed.component';
import { ContractingNotFoundComponent } from './modules/contracting/components/contracting-not-found/contracting-not-found.component';
import { LoginComponent } from './modules/auth/pages/login/login.component';
import { CreatePasswordComponent } from './modules/auth/pages/create-password/create-password.component';
import { isFirstSessionGuard } from './modules/auth/guards/is-first-session.guard';
import { RecoverPasswordComponent } from './modules/auth/pages/recover-password/recover-password.component';
import { IsDeletedGuard } from './modules/auth/guards/is-deleted.guard';
import { NoAccessComponent } from './modules/auth/pages/no-access/no-access.component';
import { IsAdmin } from './modules/auth/guards/is-admin.guard';
import { BusinessViewComponent } from './modules/dashboard/views/business-view/business-view.component';

// Guards centralizados para reutilización
const redirectToLogin = () => redirectUnauthorizedTo(['/auth/login']);
const redirectToApp = () => redirectLoggedInTo(['/app']);

// Rutas principales
export const routes: Routes = [
  // Rutas de autenticación
  {
    path: 'auth',
    component: FullpageLayoutComponent,
    children: [
      {
        path: 'login',
        canActivate: [AuthGuard],
        data: { authGuardPipe: redirectToApp },
        component: LoginComponent,
      },
      {
        path: 'alta-empleado',
        canActivate: [AuthGuard, isFirstSessionGuard],
        component: CreatePasswordComponent,
      },
      {
        path: 'recover',
        canActivate: [IsNotDeletedGuard],
        component: RecoverPasswordComponent,
      },
      {
        path: 'no-access',
        canActivate: [IsDeletedGuard],
        component: NoAccessComponent,
      },
      {
        path: '**',
        redirectTo: 'login',
        pathMatch: 'full',
      },
    ],
  },
  // Rutas de la aplicación
  {
    path: 'app',
    component: AppLoaderComponent,
    canActivate: [AuthGuard],
    data: { authGuardPipe: redirectToLogin },
    children: [
      {
        path: '',
        component: DashboardLayoutComponent,
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: 'overview',
            canActivate: [IsNotDeletedGuard],
            loadComponent: () =>
              import(
                './modules/dashboard/views/overview/overview.component'
              ).then((m) => m.OverviewComponent),
          },
          {
            path: 'clients',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all', pathMatch: 'full' },
              {
                path: 'all',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/clients/views/all-clients/all-clients.component'
                  ).then((m) => m.AllClientsComponent),
              },
              {
                path: 'client/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/clients/views/client-view/client-view.component'
                  ).then((m) => m.ClientViewComponent),
              },
            ],
          },
          {
            path: 'business',
            component: BusinessViewComponent,
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'reporting', pathMatch: 'full' },
              {
                path: 'reporting',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/daily-business-evolution/daily-business-evolution.component'
                  ).then((m) => m.DailyBusinessEvolutionComponent),
              },
              {
                path: 'detail',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/business-evolution/views/overview/overview.component'
                  ).then((m) => m.BusinessOverViewComponent),
              },
            ],
          },
          // Sección de operaciones
          {
            path: 'operations',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all-operations', pathMatch: 'full' },
              {
                path: 'all-operations',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/all-operations/all-operations.component'
                  ).then((m) => m.AllOperationsComponent),
              },
              {
                path: 'operation/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/operation/operation.component'
                  ).then((m) => m.OperationComponent),
              },
              {
                path: 'new/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/create-operation/create-operation.component'
                  ).then((m) => m.CreateOperationComponent),
              },
              {
                path: 'complete/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/create-operation/create-operation.component'
                  ).then((m) => m.CreateOperationComponent),
              },
              {
                path: 'desglose/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/upload-products/upload-products.component'
                  ).then((m) => m.UploadProductsComponent),
              },
              {
                path: 'export',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/operations/views/export-operations/export-operations.component'
                  ).then((m) => m.ExportOperationsComponent),
              },
            ],
          },
          {
            path: 'opportunities',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all', pathMatch: 'full' },
              {
                path: 'all',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/opportunities/views/all-opportunities/all-opportunities.component'
                  ).then((m) => m.AllOpportunitiesComponent),
              },
            ],
          },
          {
            path: 'chat',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'chats', pathMatch: 'full' },
              {
                path: 'chats',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/chat-view/chat-view.component'
                  ).then((m) => m.ChatViewComponent),
              },
            ],
          },
          {
            path: 'forms',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all-forms', pathMatch: 'full' },
              {
                path: 'all-forms',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/forms-view/forms-view.component'
                  ).then((m) => m.FormsViewComponent),
              },
            ],
          },
          {
            path: 'stores',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all-stores', pathMatch: 'full' },
              {
                path: 'all-stores',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/stores/views/all-stores/all-stores.component'
                  ).then((m) => m.AllStoresComponent),
              },
              {
                path: 'new',
                canActivate: [IsAdmin],
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/stores/components/new-store-form/new-store-form.component'
                  ).then((m) => m.NewStoreFormComponent),
              },
            ],
          },
          {
            path: 'cash-register',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all-registers', pathMatch: 'full' },
              {
                path: 'register/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/cash-register/view/cash-view/cash-view.component'
                  ).then((m) => m.CashViewComponent),
              },
              {
                path: 'all-registers',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/cash-register/view/all-cash-register-view/all-cash-register-view.component'
                  ).then((m) => m.AllCashRegisterViewComponent),
              },
            ],
          },
          {
            path: 'employees',
            canActivate: [IsNotDeletedGuard],
            loadComponent: () =>
              import(
                './modules/dashboard/views/employees/views/employees-view/employees-view'
              ).then((m) => m.EmployeesView),
            children: [
              {
                path: 'all-employees',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/employees/views/all-employees/all-employees.component'
                  ).then((m) => m.AllEmployeesComponent),
              },
              {
                path: 'working-hours',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/employees/views/working-hours/working-hours'
                  ).then((m) => m.WorkingHours),
                canActivate: [IsAdmin],
              },
              {
                path: 'my-profile/:id',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/profile-view/profile-view.component'
                  ).then((m) => m.ProfileViewComponent),
              },
              {
                canActivate: [IsAdmin],
                path: 'new-employee',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/employees/views/new-employee/new-employee.component'
                  ).then((m) => m.NewEmployeeComponent),
              },
              { path: '', redirectTo: 'all-employees', pathMatch: 'full' },
            ],
          },
          {
            path: 'authorizations',
            canActivate: [IsNotDeletedGuard],
            children: [
              { path: '', redirectTo: 'all-codes', pathMatch: 'full' },
              {
                path: 'all-codes',
                loadComponent: () =>
                  import(
                    './modules/dashboard/views/authorization-codes/views/allAuthCodes/allAuthCodes.component'
                  ).then((m) => m.AllAuthCodesComponent),
              },
            ],
          },
          {
            path: 'cookbook',
            canActivate: [IsNotDeletedGuard],
            loadComponent: () => import(
              './modules/dashboard/views/cookbook-view/cookbook-view'
            ).then((m) => m.CookbookView),
          }
        ],
      },
    ],
  },
  // Contrataciones
  {
    path: 'contracting',
    children: [
      {
        path: ':id',
        component: LoadingContractLayoutComponent,
        children: [
          { path: 'sign', component: NewContractComponent },
          { path: 'acceder', component: ContractingAccesComponent },
          { path: 'completed', component: ContractingCompletedComponent },
          { path: 'error', component: ContractingNotFoundComponent },
        ],
      },
      { path: '', redirectTo: '/undefined/error', pathMatch: 'full' },
    ],
  },
  // Ruta por defecto
  { path: '**', redirectTo: 'app', pathMatch: 'full' },
];
